import React, { useCallback, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import kkpService from '../../services/kkpService';
import { notify } from '../../components/common/AppToast';
import { formatPlanningImportMessage } from '../../utils/kkpPlanningImport';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import KkpPlanningStatutDisplay from '../../components/kkp/KkpPlanningStatutDisplay';
import KkpVenteNewsletterModal from '../../components/kkp/KkpVenteNewsletterModal';
import KkpPlanningMagInlineCell from '../../components/kkp/KkpPlanningMagInlineCell';
import { filterStatutsForContext, filterStatutsForMonth } from '../../utils/kkpStatutUtils';
import {
  buildNewsletterWeekColorByRowId,
} from '../../utils/kkpNewsletterWeekUtils';
import KkpPlanningStickyTableWrap from '../../components/kkp/KkpPlanningStickyTableWrap';
import { AppFilterSelect, AppFilterCommercialSelect, AppFilterField, AppFilterDateRange } from '../../components/filters';
import { AppButton } from '../../components/ui';
import KkpPlanningImportModal from '../../components/kkp/KkpPlanningImportModal';
import KkpPubImageButton from '../../components/kkp/KkpPubImageButton';
import KkpBatValideModal from '../../components/kkp/KkpBatValideModal';
import SortableTh from '../../components/common/SortableTh';
import KkpClientAutocomplete from '../../components/kkp/KkpClientAutocomplete';
import { getPlanningRowKey, mergePlanningRow } from '../../utils/kkpPlanningMergeUtils';
import { confirmVenteClear } from '../../utils/confirmVenteDelete';
import {
  buildClientInlinePayload,
  clientInlineDraftEquals,
  getClientInlineDraft,
} from '../../utils/kkpPlanningClientInline';
import { suggestBriefWizardStep, briefStatusWizardStep } from '../../utils/kkpVenteBriefUtils';
import { buildSemaineOptionsFromVentes } from '../../utils/kkpPlanningSemaineOptions';
import { filterRowsByMulti, joinOrEmpty } from '../../utils/kkpPlanningFilters';
import {
  normalizeDateFilter,
  normalizeMultiFilter,
  normalizeTextFilter,
} from '../../utils/kkpPlanningFiltersStorage';
import { usePlanningWeekAutoScroll } from '../../hooks/usePlanningWeekAutoScroll';
import {
  getPlanningClientDisplay,
  getPlanningViewEmptyMessage,
  isPlanningIndispoWithoutClient,
  PLANNING_VIEW_CLIENTS,
} from '../../utils/kkpPlanningViewMode';
import { resolveCommercialSelectionFromDolibarrClient } from '../../utils/kkpDolibarrCommercialPrefill';
import usePlanningVentesPage from '../../hooks/usePlanningVentesPage';
import KkpPlanningPageShell, { KkpPlanningEmptyRow } from '../../components/kkp/KkpPlanningPageShell';
import {
  formatAudience,
  formatTauxClick,
  parseTauxClickNumber,
  todayYmd,
  periodFromDateDebut,
  importPeriodLabelFromDateDebut,
} from '../../utils/kkpPlanningFormat';

const getBrevoCampaignUrl = (campaignId) => {
  if (!campaignId) return null;
  return `https://app.brevo.com/marketing-reports/email/${campaignId}/clicks`;
};

const getMailchimpCampaignUrl = (campaignId) => {
  if (!campaignId) return null;
  return `https://us14.admin.mailchimp.com/reports/summary?id=${campaignId}`;
};

const getCampaignLinks = (vente) => {
  const links = [];
  if (vente.brevo_campaign_id) {
    links.push({
      url: getBrevoCampaignUrl(vente.brevo_campaign_id),
      icon: 'simple-icons:brevo',
      cls: 'btn-outline-success',
      label: 'Brevo',
    });
  }
  if (vente.mailchimp_campaign_id) {
    links.push({
      url: getMailchimpCampaignUrl(vente.mailchimp_campaign_id),
      icon: 'simple-icons:mailchimp',
      cls: 'btn-outline-info',
      label: 'Mailchimp',
    });
  }
  return links.filter((link) => link.url);
};

const getDefaultNewsletterFilters = () => ({
  date_debut: todayYmd(),
  date_fin: '',
  commercial_id: [],
  type_news: [],
  emplacement: [],
  statut_phase: [],
  search: '',
});

const NEWSLETTER_FILTERS_STORAGE_KEY = 'kkp_planning_newsletter_filters';

const normalizeNewsletterFilters = (savedFilters) => {
  const defaults = getDefaultNewsletterFilters();
  const source = savedFilters && typeof savedFilters === 'object' ? savedFilters : {};

  return {
    date_debut: normalizeDateFilter(source.date_debut, defaults.date_debut),
    date_fin: normalizeDateFilter(source.date_fin, ''),
    commercial_id: normalizeMultiFilter(source.commercial_id),
    type_news: normalizeMultiFilter(source.type_news),
    emplacement: normalizeMultiFilter(source.emplacement),
    statut_phase: normalizeMultiFilter(source.statut_phase),
    search: normalizeTextFilter(source.search, ''),
  };
};

const toNewsletterApiFilters = (filters) => ({
  date_debut: filters.date_debut ?? '',
  date_fin: filters.date_fin ?? '',
  commercial_id: joinOrEmpty(filters.commercial_id),
  type_news: joinOrEmpty(filters.type_news),
  emplacement: joinOrEmpty(filters.emplacement),
  statut_phase: joinOrEmpty(filters.statut_phase),
  search: filters.search ?? '',
});

const TABLE_SORT_COLUMNS = {
  periode: { type: 'number', getValue: (row) => (row.annee ?? 0) * 100 + (row.mois ?? 0) },
  semaine: { type: 'date', getValue: (row) => row.semaine_date || '' },
  commercial: { type: 'string', getValue: (row) => row.commercial_nom },
  client: { type: 'string', getValue: (row) => getPlanningClientDisplay(row, '') },
  emplacement: { type: 'string', getValue: (row) => row.emplacement },
  statut: { type: 'string', getValue: (row) => row.statut_phase },
  commentaire: { type: 'string', getValue: (row) => row.commentaire },
  newsRecues: { type: 'number', getValue: (row) => row.news_recues ?? 0 },
  newsOuvertes: { type: 'number', getValue: (row) => row.news_ouvertes ?? 0 },
  pubCliquees: { type: 'number', getValue: (row) => row.pub_cliquees ?? 0 },
  tauxClick: { type: 'number', getValue: (row) => parseTauxClickNumber(row.taux_click) ?? 0 },
  typeNews: { type: 'string', getValue: (row) => row.type_news },
};

const KkpPlanningNewsletterPage = () => {
  const [syncingAudience, setSyncingAudience] = useState(false);

  const fetchMeta = useCallback(() => kkpService.getNewsletterMeta(), []);
  const initFiltersFromMeta = useCallback((_meta, savedFilters) => normalizeNewsletterFilters(savedFilters), []);
  const fetchVentes = useCallback(async (filters, { page }) => {
    const apiFilters = toNewsletterApiFilters(filters);
    const gridMode = Boolean(apiFilters.date_debut)
      && !apiFilters.search
      && !apiFilters.commercial_id
      && !apiFilters.statut_phase
      && (filters.commercial_id?.length ?? 0) <= 1
      && (filters.statut_phase?.length ?? 0) <= 1;
    const limit = gridMode ? 1000 : 50;
    const result = await kkpService.listVentesNewsletter({ ...apiFilters, page, limit });
    let nextVentes = result.ventes;
    nextVentes = filterRowsByMulti(nextVentes, filters.commercial_id, (v) => v.commercial_id);
    nextVentes = filterRowsByMulti(nextVentes, filters.type_news, (v) => v.type_news ?? v.contenu);
    nextVentes = filterRowsByMulti(nextVentes, filters.emplacement, (v) => v.emplacement ?? v.format);
    nextVentes = filterRowsByMulti(nextVentes, filters.statut_phase, (v) => v.statut_phase);
    return {
      ventes: nextVentes,
      pagination: result.pagination,
      extras: { moyenneHebdo: result.moyenne_hebdo ?? null },
    };
  }, []);

  const {
    ask,
    confirmModal,
    meta,
    ventes,
    setVentes,
    commerciaux,
    defaultCommercialId,
    pagination,
    filters,
    setFilters,
    loading,
    error,
    setError,
    showModal,
    editingVente,
    modalInitialValues,
    modalWizardStep,
    openCreate,
    openEdit,
    closeModal,
    setImporting,
    setExporting,
    showImportModal,
    setShowImportModal,
    importStrategy,
    setImportStrategy,
    wipeScope,
    setWipeScope,
    fileInputRef,
    editingCell,
    setEditingCell,
    editDraft,
    setEditDraft,
    inlineSaving,
    setInlineSaving,
    batValideVente,
    batValideSubmitting,
    batValideError,
    openBatValide,
    closeBatValide,
    submitBatValide,
    statutSelectProps,
    viewMode,
    setViewMode,
    changeViewMode,
    viewModeCounts,
    visibleVentes,
    sortedRows,
    sort,
    toggleSort,
    getSortIcon,
    commercialOptions,
    loadMeta,
    loadVentes,
    findVenteByRowId,
    getRowId,
    listExtras,
    buildImportExportMenu,
  } = usePlanningVentesPage({
    storageKey: NEWSLETTER_FILTERS_STORAGE_KEY,
    tableSortColumns: TABLE_SORT_COLUMNS,
    defaultSort: { key: 'semaine', direction: 'asc' },
    fetchMeta,
    initFiltersFromMeta,
    fetchVentes,
  });

  const moyenneHebdo = listExtras?.moyenneHebdo ?? null;

  const filterStatuts = useMemo(
    () => filterStatutsForContext(meta?.statuts, null, null),
    [meta?.statuts],
  );

  const newsletterWeekColorByRowId = useMemo(
    () => buildNewsletterWeekColorByRowId(ventes, getPlanningRowKey),
    [ventes],
  );

  const semaineOptions = useMemo(
    () => {
      const years = new Set();
      const addYear = (value) => {
        const year = Number(String(value || '').slice(0, 4));
        if (year >= 2000 && year <= 2100) {
          years.add(year);
        }
      };
      addYear(filters?.date_debut);
      addYear(filters?.date_fin);
      years.add(new Date().getFullYear());
      ventes.forEach((vente) => {
        if (vente?.annee) {
          years.add(Number(vente.annee));
        }
      });

      return buildSemaineOptionsFromVentes(ventes, {
        annees: Array.from(years),
        canal: 'newsletter',
      });
    },
    [ventes, filters?.date_debut, filters?.date_fin],
  );

  const moisOptions = useMemo(
    () => (meta?.mois ?? []).map((m) => ({ value: String(m.value), label: m.label })),
    [meta?.mois],
  );
  const typeNewsOptions = useMemo(
    () => (meta?.types_news ?? []).map((t) => ({ value: t, label: t })),
    [meta?.types_news],
  );
  const emplacementOptions = useMemo(
    () => (meta?.formats ?? []).map((f) => ({ value: f.value, label: f.value })),
    [meta?.formats],
  );
  const statutOptions = useMemo(
    () => filterStatuts.map((s) => ({ value: s.value, label: s.value })),
    [filterStatuts],
  );

  const { setRowRef, getAnchorRowProps } = usePlanningWeekAutoScroll({
    ventes,
    loading,
    filters: filters ?? getDefaultNewsletterFilters(),
    getRowKey: getPlanningRowKey,
  });

  const handleOpenCreate = () => {
    const start = filters?.date_debut ? new Date(`${filters.date_debut}T00:00:00`) : new Date();
    openCreate({
      annee: start.getFullYear(),
      mois: start.getMonth() + 1,
    });
  };

  const handleCreaPubChange = (venteId, pieces) => {
    setVentes((prev) => prev.map((row) => (
      row.id === venteId ? { ...row, crea_pub_pieces_jointes: pieces } : row
    )));
  };

  const handleOpenEdit = (vente, wizardStep = undefined) => {
    openEdit(vente, typeof wizardStep === 'number' ? wizardStep : suggestBriefWizardStep(vente));
  };

  const handleClear = async (venteOrId) => {
    const vente = typeof venteOrId === 'object'
      ? venteOrId
      : findVenteByRowId(venteOrId) ?? ventes.find((v) => v.id === venteOrId);
    const id = vente?.id ?? venteOrId;
    if (!id) return;
    if (!(await confirmVenteClear(ask, vente?.client_label))) return;
    try {
      await kkpService.clearVenteNewsletter(id);
      loadVentes(pagination.page || 1);
    } catch (err) {
      setError(err.message);
    }
  };
  const handleDelete = handleClear;

  const getInlineDraft = (vente, field) => {
    switch (field) {
      case 'periode':
        return { annee: vente.annee, mois: vente.mois };
      case 'semaine':
        return vente.semaine ?? '';
      case 'commercial_id':
        return vente.commercial_id != null ? String(vente.commercial_id) : '';
      case 'client_label':
        return getClientInlineDraft(vente);
      case 'emplacement':
        return vente.emplacement ?? vente.format ?? '';
      case 'type_news':
        return vente.type_news ?? vente.contenu ?? '';
      case 'statut_phase':
        return vente.statut_phase ?? '';
      case 'commentaire':
        return vente.commentaire ?? '';
      default:
        return '';
    }
  };

  const startInlineEdit = async (venteId, field) => {
    if (!venteId || !field) {
      setEditingCell(null);
      setEditDraft(null);
      return;
    }
    if (editingCell && (editingCell.venteId !== venteId || editingCell.field !== field)) {
      await saveInlineEdit();
    }
    const vente = findVenteByRowId(venteId);
    if (!vente) return;
    setEditingCell({ venteId, field });
    setEditDraft(getInlineDraft(vente, field));
  };

  const buildInlinePayload = (field, draft) => {
    switch (field) {
      case 'periode':
        return { annee: draft.annee, mois: draft.mois };
      case 'semaine':
        return { semaine: draft || null };
      case 'commercial_id':
        return { commercial_id: draft || null };
      case 'client_label':
        return buildClientInlinePayload(draft);
      case 'emplacement':
        return { emplacement: draft };
      case 'type_news':
        return { type_news: draft ?? '' };
      case 'statut_phase':
        return { statut_phase: draft ?? '' };
      case 'commentaire':
        return { commentaire: draft || null };
      default:
        return {};
    }
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const vente = findVenteByRowId(editingCell.venteId);
    if (!vente) return;

    const draft = draftOverride !== undefined ? draftOverride : editDraft;
    const original = getInlineDraft(vente, editingCell.field);
    const unchanged = editingCell.field === 'client_label'
      ? clientInlineDraftEquals(draft, vente)
      : JSON.stringify(draft) === JSON.stringify(original);
    if (unchanged) {
      setEditingCell(null);
      setEditDraft(null);
      return;
    }

    setInlineSaving(true);
    setError(null);
    try {
      const payload = buildInlinePayload(editingCell.field, draft);

      if (vente.is_placeholder) {
        const createPayload = {
          annee: vente.annee,
          mois: vente.mois,
          semaine: vente.semaine,
          emplacement: vente.emplacement ?? vente.format,
          type_news: vente.type_news ?? vente.contenu,
          statut_phase: 'Option',
          client_label: '',
          ...payload,
        };
        const created = await kkpService.createVenteNewsletter(createPayload);
        setVentes((prev) => mergePlanningRow(prev, created, { replaceKey: getRowId(vente) }));
      } else {
        const updated = await kkpService.updateVenteNewsletter(vente.id, payload);
        setVentes((prev) => mergePlanningRow(prev, updated));
      }

      setEditingCell(null);
      setEditDraft(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const currentPeriod = () => periodFromDateDebut(filters?.date_debut || todayYmd());

  const importPeriodLabel = useMemo(
    () => importPeriodLabelFromDateDebut(filters?.date_debut, moisOptions),
    [filters?.date_debut, moisOptions],
  );

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const { annee, mois } = currentPeriod();
      await kkpService.exportVentesNewsletter({ annee, mois });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleSyncAudience = async () => {
    setSyncingAudience(true);
    setError(null);
    try {
      const { annee, mois } = currentPeriod();
      const result = await kkpService.syncNewsletterAudience({ annee, mois });
      const parts = [`${result.updated} ligne(s) mise(s) à jour`];
      if (result.unmatched > 0) parts.push(`${result.unmatched} campagne(s) non matchée(s)`);
      if (result.errors > 0) parts.push(`${result.errors} erreur(s)`);
      const msg = `Sync audience (cutoff ${result.brevo_cutoff_date || '2026-07-24'}) : ${parts.join(', ')}.`;
      if (result.errors > 0) {
        notify.warning(msg);
      } else if (result.updated > 0) {
        notify.success(msg);
      } else {
        notify.info(msg);
      }
      loadVentes(pagination.page);
    } catch (err) {
      setError(err.message);
      notify.error(err.message);
    } finally {
      setSyncingAudience(false);
    }
  };

  const handleImportClick = () => {
    setImportStrategy('upsert');
    setWipeScope(filters?.date_debut ? 'month' : 'all');
    setShowImportModal(true);
  };

  const handleImportConfirm = () => {
    if (importStrategy === 'wipe' && wipeScope === 'month' && !filters?.date_debut) {
      setError('Choisissez une date de début pour indiquer le mois à remplacer.');
      return;
    }
    setShowImportModal(false);
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImporting(true);
    setError(null);
    try {
      const { annee, mois } = currentPeriod();
      const result = await kkpService.importVentesNewsletter(file, {
        upsert: importStrategy === 'upsert',
        replace: importStrategy === 'wipe' && wipeScope === 'month',
        replaceAll: importStrategy === 'wipe' && wipeScope === 'all',
        annee: importStrategy === 'wipe' && wipeScope === 'month' ? annee : '',
        mois: importStrategy === 'wipe' && wipeScope === 'month' ? mois : '',
      });
      const msg = formatPlanningImportMessage(result);
      if ((result.errors?.length ?? 0) > 0) {
        notify.warning(`${msg} ${result.errors.length} erreur(s) — voir la console.`);
        console.warn('Import Newsletter erreurs', result.errors);
      } else {
        notify.success(msg);
      }
      await loadMeta();
      loadVentes(1);
    } catch (err) {
      setError(err.message);
      notify.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  const resetNewsletterFilters = () => {
    setViewMode(PLANNING_VIEW_CLIENTS);
    setFilters({
      ...getDefaultNewsletterFilters(),
      commercial_id: [],
    });
  };

  return (
    <KkpPlanningPageShell
      title="Planning ventes Newsletters"
      subtitle="Suivi des ventes newsletters à partir d'aujourd'hui, par emplacement et type"
      actions={[
        {
          key: 'sync',
          label: syncingAudience ? 'Sync…' : 'Stats audience',
          icon: 'mdi:email-sync',
          onClick: handleSyncAudience,
          disabled: syncingAudience,
          loading: syncingAudience,
        },
      ]}
      menuItems={buildImportExportMenu(handleImportClick, handleExport)}
      error={error}
      kpi={moyenneHebdo ? (
        <div className="card mb-24 kkp-planning-kpi-card">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center gap-3">
              <span className="kkp-planning-kpi-card__label mb-0">Moyenne hebdo</span>
              {moyenneHebdo.semaines_avec_donnees > 0 ? (
                <>
                  <span className="text-sm text-secondary-light">
                    News reçues : <strong>{formatAudience(moyenneHebdo.news_recues)}</strong>
                  </span>
                  <span className="text-sm text-secondary-light">
                    News ouvertes : <strong>{formatAudience(moyenneHebdo.news_ouvertes)}</strong>
                  </span>
                  <span className="text-sm text-secondary-light">
                    Taux d&apos;ouvertures : <strong>{formatTauxClick(moyenneHebdo.taux_ouverture)}</strong>
                  </span>
                  <span className="text-sm text-secondary-light">
                    Pub cliquées : <strong>{formatAudience(moyenneHebdo.pub_cliquees)}</strong>
                  </span>
                  <span className="text-sm text-secondary-light">
                    Taux de click : <strong>{formatTauxClick(moyenneHebdo.taux_click)}</strong>
                  </span>
                </>
              ) : (
                <span className="text-sm text-secondary-light">Aucune donnée audience sur cette période.</span>
              )}
            </div>
          </div>
        </div>
      ) : null}
      fileInputRef={fileInputRef}
      onImportFile={handleImportFile}
      resetDisabled={!filters}
      onResetFilters={resetNewsletterFilters}
      filterChildren={(
        <>
          <AppFilterDateRange
            id="nl-periode"
            label="Période d'envoi"
            col="col-12 col-md-5 col-lg-4"
            dateDebut={filters?.date_debut ?? todayYmd()}
            dateFin={filters?.date_fin ?? ''}
            onChange={({ dateDebut, dateFin }) => setFilters((f) => ({
              ...f,
              date_debut: dateDebut || todayYmd(),
              date_fin: dateFin || '',
            }))}
            disabled={!filters}
          />
          <AppFilterCommercialSelect
            id="nl-commercial"
            col="col-6 col-md-2"
            value={filters?.commercial_id ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, commercial_id: next }))}
            options={commercialOptions}
            disabled={!filters}
            multiple
          />
          <AppFilterSelect
            id="nl-type"
            label="Type"
            col="col-6 col-md-2"
            value={filters?.type_news ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, type_news: next }))}
            options={typeNewsOptions}
            disabled={!filters}
          />
          <AppFilterSelect
            id="nl-emplacement"
            label="Emplacement"
            col="col-6 col-md-2"
            value={filters?.emplacement ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, emplacement: next }))}
            options={emplacementOptions}
            disabled={!filters}
          />
          <AppFilterSelect
            id="nl-statut"
            label="Statut"
            col="col-6 col-md-2"
            value={filters?.statut_phase ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, statut_phase: next }))}
            options={statutOptions}
            disabled={!filters}
          />
          <AppFilterField col="col-12 col-md-4" label="Client">
            <KkpClientAutocomplete
              value={filters?.search ?? ''}
              onChange={(val) => setFilters((f) => ({ ...f, search: val }))}
              onSelect={async (client) => {
                const label = String(client?.nom ?? client?.label ?? '').trim();
                try {
                  const matchedCommercial = await resolveCommercialSelectionFromDolibarrClient(client, commercialOptions);
                  setFilters((f) => ({
                    ...f,
                    search: label,
                    commercial_id: matchedCommercial ? [matchedCommercial.value] : [],
                  }));
                } catch {
                  setFilters((f) => ({ ...f, search: label }));
                }
              }}
              disabled={!filters}
              size="sm"
              placeholder="Client ou tiers Dolibarr…"
            />
          </AppFilterField>
        </>
      )}
      viewMode={viewMode}
      onViewModeChange={changeViewMode}
      viewModeCounts={viewModeCounts}
      onCreate={handleOpenCreate}
      loading={loading}
      table={(
        <KkpPlanningStickyTableWrap>
          <table className="table table-hover mb-0 kkp-planning-table kkp-planning-table--newsletter">
            <thead>
              <tr>
                <SortableTh sortKey="periode" label="Période" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="semaine" label="Semaine" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="client" label="Client" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="emplacement" label="Emplacement" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="commentaire" label="Commentaire" className="kkp-table-comment" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="newsRecues" label="Reçues" className="text-end" title="News reçues" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="newsOuvertes" label="Ouvertes" className="text-end" title="News ouvertes" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="pubCliquees" label="Clics" className="text-end" title="Pub cliquées" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="tauxClick" label="Taux" className="text-end" title="Taux de click" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="typeNews" label="Type news" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh label="Actions" className="text-end kkp-col-actions" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {visibleVentes.length === 0 ? (
                <KkpPlanningEmptyRow
                  colSpan={13}
                  message={getPlanningViewEmptyMessage(viewMode)}
                  actions={[
                    {
                      key: 'reset',
                      label: 'Réinitialiser',
                      icon: 'mdi:filter-off-outline',
                      onClick: resetNewsletterFilters,
                    },
                    {
                      key: 'create',
                      label: 'Nouvelle vente',
                      icon: 'mdi:plus',
                      variant: 'primary',
                      onClick: handleOpenCreate,
                    },
                  ]}
                />
              ) : sortedRows.map((v) => {
                const rowKey = getPlanningRowKey(v);
                const anchorProps = getAnchorRowProps(rowKey);
                return (
                  <tr
                    key={rowKey}
                    ref={(node) => setRowRef(rowKey, node)}
                    className={[
                      v.is_placeholder ? 'table-light' : '',
                      anchorProps.className,
                    ].filter(Boolean).join(' ') || undefined}
                    style={anchorProps.style}
                  >
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="periode"
                      type="periode"
                      options={meta?.mois ?? []}
                      optionValueKey="value"
                      optionLabelKey="label"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                    >
                      <span className="text-sm">{v.mois_label} {v.annee}</span>
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="semaine"
                      type="select"
                      options={semaineOptions}
                      optionValueKey="value"
                      optionLabelKey="label"
                      allowEmpty={false}
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className={`kkp-week-semaine-cell kkp-week-semaine-cell--${newsletterWeekColorByRowId.get(getPlanningRowKey(v)) ?? 0}`}
                    >
                      <span className="text-sm">{v.semaine || '—'}</span>
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="commercial_id"
                      type="select"
                      options={commerciaux}
                      optionValueKey="id"
                      optionLabelKey="nom"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                    >
                      {v.commercial_nom ? (
                        <KkpColorBadge color={v.commercial_couleur} vivid>
                          {v.commercial_nom}
                        </KkpColorBadge>
                      ) : (
                        <span className="text-secondary-light">—</span>
                      )}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="client_label"
                      type="client"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="fw-medium"
                    >
                      <span className={isPlanningIndispoWithoutClient(v) ? 'text-secondary-light fst-italic' : undefined}>
                        {getPlanningClientDisplay(v)}
                      </span>
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="emplacement"
                      type="select"
                      allowEmpty={false}
                      options={meta?.formats ?? []}
                      optionValueKey="value"
                      optionLabelKey="value"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                    >
                      <span className="text-sm">{v.emplacement || v.format}</span>
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="statut_phase"
                      type="select"
                      options={filterStatutsForMonth(meta?.statuts, v.annee, v.mois)}
                      optionValueKey="value"
                      optionLabelKey="value"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      {...statutSelectProps(v)}
                    >
                      {v.statut_phase ? (
                        <KkpPlanningStatutDisplay
                          vente={v}
                          onBriefClick={(briefStatus) => handleOpenEdit(v, briefStatusWizardStep(v, briefStatus))}
                        />
                      ) : (
                        <span className="text-secondary-light">—</span>
                      )}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="commentaire"
                      type="text"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-sm text-secondary-light kkp-table-comment"
                    >
                      {v.commentaire || '—'}
                    </KkpPlanningMagInlineCell>
                    <td className="text-end text-sm">
                      {formatAudience(v.news_recues)}
                      {(v.audience_source === 'mailchimp' || v.audience_source === 'brevo') && (
                        <Icon
                          icon="mdi:cloud-check"
                          className={`ms-1 ${v.audience_source === 'brevo' ? 'text-success' : 'text-info'}`}
                          title={`Sync auto (${v.audience_source})${v.audience_synced_at ? ` — ${new Date(v.audience_synced_at).toLocaleString('fr-FR')}` : ''}`}
                        />
                      )}
                    </td>
                    <td className="text-end text-sm">{formatAudience(v.news_ouvertes)}</td>
                    <td className="text-end text-sm">{formatAudience(v.pub_cliquees)}</td>
                    <td className="text-end text-sm">{formatTauxClick(v.taux_click)}</td>
                    <KkpPlanningMagInlineCell
                      venteId={getRowId(v)}
                      field="type_news"
                      type="select"
                      options={(meta?.types_news ?? []).map((t) => ({ value: t, label: t }))}
                      optionValueKey="value"
                      optionLabelKey="label"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                    >
                      <span className="text-sm">{v.type_news || v.contenu}</span>
                    </KkpPlanningMagInlineCell>
                    <td className="text-end kkp-col-actions">
                      {v.id ? (
                        <div className="kkp-actions-toolbar">
                          {getCampaignLinks(v).map((link) => (
                            <a
                              key={link.label}
                              href={link.url}
                              target="_blank"
                              rel="noreferrer"
                              className={`btn btn-sm ${link.cls}`}
                              title={`Ouvrir la campagne ${link.label}`}
                              aria-label={`Ouvrir la campagne ${link.label}`}
                            >
                              <Icon icon={link.icon} />
                            </a>
                          ))}
                          <KkpPubImageButton vente={v} onCreaPubChange={handleCreaPubChange} />
                          <AppButton
                            size="sm"
                            variant="outline-primary"
                            icon="mdi:pencil"
                            title="Modifier"
                            aria-label="Modifier"
                            onClick={() => handleOpenEdit(v)}
                          />
                          <AppButton
                            size="sm"
                            variant="outline-warning"
                            icon="mdi:eraser"
                            title="Effacer le contenu (conserve l'emplacement)"
                            aria-label="Effacer"
                            onClick={() => handleDelete(v)}
                          />
                        </div>
                      ) : (
                        <span className="text-secondary-light kkp-actions-hint">Double-clic pour remplir</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </KkpPlanningStickyTableWrap>
      )}
      pagination={pagination}
      onPageChange={(page) => loadVentes(page)}
      modals={(
        <>
          <KkpVenteNewsletterModal
            show={showModal}
            onClose={closeModal}
            onSaved={() => loadVentes(pagination.page || 1)}
            onDeleted={() => loadVentes(pagination.page || 1)}
            onError={setError}
            meta={meta}
            defaultCommercialId={defaultCommercialId}
            vente={editingVente}
            initialValues={modalInitialValues}
            initialWizardStep={modalWizardStep}
            onRequestBatValide={(nextVente) => {
              closeModal();
              openBatValide(nextVente);
            }}
          />

          <KkpBatValideModal
            show={Boolean(batValideVente)}
            vente={batValideVente}
            onClose={closeBatValide}
            onSubmit={submitBatValide}
            submitting={batValideSubmitting}
            error={batValideError}
          />

          <KkpPlanningImportModal
            show={showImportModal}
            title="Importer le planning Newsletters"
            sheetHint="La feuille VENTE NEWLETTERS du fichier sera lue automatiquement."
            canalLabel="Newsletters"
            strategy={importStrategy}
            onStrategyChange={setImportStrategy}
            wipeScope={wipeScope}
            onWipeScopeChange={setWipeScope}
            periodLabel={importPeriodLabel}
            onClose={() => setShowImportModal(false)}
            onConfirm={handleImportConfirm}
          />
          {confirmModal}
        </>
      )}
    />
  );
};

export default KkpPlanningNewsletterPage;
